import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  FormControl,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type { IncidentGroupHeatmapResponse } from '../../services/incidentsApi';
import {
  formatHeatmapCellCountLabel,
  formatHeatmapRowActiveHoursLabel,
  heatmapCellCountTooltip,
  RECURRING_RISK_HEATMAP_EMPTY_MESSAGE,
} from '../../domain/incidents/incidentPatternVocabulary';

type IncidentTypeOption = {
  value: string;
  label: string;
};

export interface RecurringRiskHeatmapCardProps {
  data: IncidentGroupHeatmapResponse | null;
  dimension: 'zone' | 'camera';
  incidentType: string;
  incidentTypeOptions: IncidentTypeOption[];
  zoneNameById: Map<string, string>;
  cameraNameById: Map<string, string>;
  onDimensionChange: (value: 'zone' | 'camera') => void;
  onIncidentTypeChange: (value: string) => void;
  onCellSelect: (target: { dimension: 'zone' | 'camera'; id: string; hour: number }) => void;
  windowDays?: 1 | 7 | 30;
  onWindowDaysChange?: (value: 1 | 7 | 30) => void;
  showScopeControls?: boolean;
  /** Overrides default “open Incidents list” caption when embedded in pattern review. */
  cellSelectCaption?: string;
  /** When true, cell click only selects; use the Open occurrence button to navigate. */
  selectOnlyOnCellClick?: boolean;
  openingOccurrence?: boolean;
}

const UNASSIGNED_ID = '__unassigned__';
const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BAR_MAX_PX = 88;
const BAR_MIN_ACTIVE_PX = 18;

const hourLabel = (value: number): string => `${String(value).padStart(2, '0')}:00`;
const buyerZoneLabel = (value: string): string => (/demo5tes/i.test(String(value || '').trim()) ? 'Restricted Forklift Zone' : value);

/** Prefer registry/name labels; never show a raw UUID as the primary label in camera view */
function formatCameraRowLabel(id: string, fromMap: string | undefined): string {
  if (id === UNASSIGNED_ID) return 'Unassigned camera';
  const resolved = String(fromMap || '').trim();
  if (resolved && resolved !== id) return resolved;
  if (UUID_LIKE.test(id)) {
    return `Camera · ${id.slice(-4)}`;
  }
  const short = id.includes('_') ? id.split('_').filter(Boolean).slice(-2).join(' · ') : id;
  if (short.length <= 32) return `Camera · ${short}`;
  return `Camera · ${id.slice(0, 8)}…`;
}

const barFill = (count: number, maxCount: number): string => {
  if (count <= 0 || maxCount <= 0) return '#e8ecf1';
  const ratio = Math.max(0, Math.min(count / maxCount, 1));
  if (ratio <= 0.34) return '#fdba74';
  if (ratio <= 0.67) return '#ea580c';
  return '#c2410c';
};

const RecurringRiskHeatmapCard: React.FC<RecurringRiskHeatmapCardProps> = ({
  data,
  dimension,
  incidentType,
  incidentTypeOptions,
  zoneNameById,
  cameraNameById,
  onDimensionChange,
  onIncidentTypeChange,
  onCellSelect,
  windowDays,
  onWindowDaysChange,
  showScopeControls = true,
  cellSelectCaption,
  selectOnlyOnCellClick = false,
  openingOccurrence = false,
}) => {
  const [selectedKey, setSelectedKey] = useState<string>('');
  const hours = useMemo(() => (Array.isArray(data?.hours) && data?.hours.length ? data?.hours : Array.from({ length: 24 }, (_, index) => index)), [data]);

  const rows = useMemo(() => {
    const source = Array.isArray(data?.rows) ? (data?.rows ?? []) : [];
    return source.map((row) => {
      const counts = new Map<number, number>();
      const activeDays = new Map<number, number>();
      (row.cells || []).forEach((cell) => {
        const hour = Number(cell.hour || 0);
        counts.set(hour, Number(cell.count || 0));
        activeDays.set(hour, Number(cell.active_days || 0));
      });
      const activeHourList = hours.filter((hour) => Number(counts.get(hour) || 0) > 0);
      return {
        ...row,
        counts,
        activeDays,
        activeHours: activeHourList.length,
        activeHourList,
      };
    });
  }, [data, hours]);

  const maxCount = useMemo(() => {
    let current = 0;
    rows.forEach((row) => {
      row.counts.forEach((value) => {
        if (value > current) current = value;
      });
    });
    return current;
  }, [rows]);

  const responseWindowDayCount = useMemo(() => {
    const from = Date.parse(String(data?.from_ts || ''));
    const to = Date.parse(String(data?.to_ts || ''));
    if (Number.isFinite(from) && Number.isFinite(to) && to > from) {
      return Math.max(1, Math.round((to - from) / (24 * 60 * 60 * 1000)));
    }
    return 7;
  }, [data?.from_ts, data?.to_ts]);
  const windowDayCount = windowDays || responseWindowDayCount;

  const labelForRow = useCallback((id: string): string => {
    if (id === UNASSIGNED_ID) return dimension === 'zone' ? 'Unassigned zone' : 'Unassigned camera';
    if (dimension === 'zone') return buyerZoneLabel(zoneNameById.get(id) || id);
    return formatCameraRowLabel(id, cameraNameById.get(id));
  }, [cameraNameById, dimension, zoneNameById]);

  const handleHourSelect = useCallback((rowId: string, hour: number, count: number) => {
    if (count <= 0 || openingOccurrence) return;
    const cellKey = `${rowId}:${hour}`;
    setSelectedKey(cellKey);
    if (!selectOnlyOnCellClick) {
      onCellSelect({ dimension, id: rowId, hour });
    }
  }, [dimension, onCellSelect, openingOccurrence, selectOnlyOnCellClick]);

  const selectionSummary = useMemo(() => {
    if (!selectedKey) return null;
    const [rowId, hourStr] = selectedKey.split(':');
    const h = Number(hourStr);
    if (!rowId || !Number.isFinite(h)) return null;
    const row = rows.find((r) => r.id === rowId);
    if (!row) return null;
    const count = Number(row.counts.get(h) || 0);
    const activeDays = Number(row.activeDays.get(h) || 0);
    return { rowId, h, count, activeDays, label: labelForRow(rowId) };
  }, [labelForRow, rows, selectedKey]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        height: '100%',
        boxShadow: '0 2px 14px rgba(15, 23, 42, 0.07)',
        borderColor: 'divider',
        borderWidth: 1,
      }}
    >
      <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
            When does this repeat?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {/* LOCAL-HOUR RECURRENCE REQUIRES DATA SUPPORT: backend heatmap buckets are UTC hours. Do not relabel as local. */}
            Occurrences by UTC hour · last {windowDayCount} {windowDayCount === 1 ? 'day' : 'days'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {onWindowDaysChange ? (
            <ToggleButtonGroup
              value={windowDayCount}
              exclusive
              size="small"
              aria-label="Recurrence window"
              onChange={(_, value) => value && onWindowDaysChange(value as 1 | 7 | 30)}
            >
              <ToggleButton value={1} aria-label="1 day">1 day</ToggleButton>
              <ToggleButton value={7} aria-label="7 days">7 days</ToggleButton>
              <ToggleButton value={30} aria-label="30 days">30 days</ToggleButton>
            </ToggleButtonGroup>
          ) : null}
          {showScopeControls ? (
            <>
              <ToggleButtonGroup
                value={dimension}
                exclusive
                size="small"
                onChange={(_, value) => value && onDimensionChange(value)}
              >
                <ToggleButton value="zone">Zone</ToggleButton>
                <ToggleButton value="camera">Camera</ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 210 }}>
                <Select
                  value={incidentType}
                  displayEmpty
                  onChange={(event) => onIncidentTypeChange(String(event.target.value || ''))}
                >
                  <MenuItem value="">All incident types</MenuItem>
                  {incidentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : null}
        </Box>
      </Box>

      {rows.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {rows.map((row) => (
            <Box key={row.id}>
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {labelForRow(row.id)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {formatHeatmapRowActiveHoursLabel(row.activeHours)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${hours.length}, minmax(22px, 1fr))`,
                  gap: 0.5,
                  alignItems: 'end',
                  minHeight: BAR_MAX_PX + 36,
                  minWidth: 720,
                  overflowX: 'auto',
                  px: 0.25,
                }}
              >
                  {hours.map((hour) => {
                    const count = Number(row.counts.get(hour) || 0);
                    const activeDayCount = Number(row.activeDays.get(hour) || 0);
                    const clickable = count > 0;
                    const cellKey = `${row.id}:${hour}`;
                    const selected = selectedKey === cellKey;
                    const heightPx = count <= 0 || maxCount <= 0
                      ? 4
                      : Math.max(BAR_MIN_ACTIVE_PX, Math.round((count / maxCount) * BAR_MAX_PX));
                    const fill = barFill(count, maxCount);
                    return (
                      <Box key={`${row.id}:${hour}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: 10,
                            fontWeight: 800,
                            color: count > 0 ? '#9a3412' : 'transparent',
                            lineHeight: 1,
                            minHeight: 12,
                          }}
                        >
                          {count > 0 ? count : '0'}
                        </Typography>
                        <Tooltip
                          title={heatmapCellCountTooltip({
                            rowLabel: labelForRow(row.id),
                            hour,
                            occurrenceCount: count,
                            activeDayCount,
                            windowDayCount,
                          })}
                        >
                          <span style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <ButtonBase
                              disabled={!clickable || openingOccurrence}
                              aria-label={`Hourly pattern ${dimension} ${labelForRow(row.id)} hour ${hour} count ${count}`}
                              onClick={() => handleHourSelect(row.id, hour, count)}
                              sx={{
                                width: '100%',
                                maxWidth: 28,
                                height: heightPx,
                                borderRadius: 0.75,
                                border: selected ? '2px solid #7c2d12' : '1px solid rgba(15, 23, 42, 0.12)',
                                backgroundColor: fill,
                                boxShadow: selected
                                  ? '0 0 0 2px rgba(194, 65, 12, 0.25)'
                                  : count > 0
                                    ? 'inset 0 1px 0 rgba(255,255,255,0.25)'
                                    : 'none',
                                '&:hover': clickable
                                  ? { transform: 'translateY(-1px)', borderColor: '#9a5800' }
                                  : undefined,
                              }}
                            />
                          </span>
                        </Tooltip>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}
                        >
                          {String(hour).padStart(2, '0')}
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {RECURRING_RISK_HEATMAP_EMPTY_MESSAGE}
        </Typography>
      )}

      {selectionSummary && selectionSummary.count > 0 ? (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
              Selected hour
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
              {selectionSummary.label} · {hourLabel(selectionSummary.h)} UTC ·{' '}
              {formatHeatmapCellCountLabel(selectionSummary.count)} ·{' '}
              active on {selectionSummary.activeDays} of {windowDayCount} days
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            disabled={openingOccurrence}
            onClick={() => onCellSelect({ dimension, id: selectionSummary.rowId, hour: selectionSummary.h })}
          >
            {openingOccurrence ? 'Opening…' : (cellSelectCaption ? 'Open occurrence' : 'Open in Incident Ops')}
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
};

export default RecurringRiskHeatmapCard;
